import React, { useEffect, useMemo, useState } from "react";
import SiteHeader from "../components/SiteHeader";
import { hasSupabaseConfig, supabase } from "../../lib/supabaseClient";

function calculateAge(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  const dob = new Date(dateStr);
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age;
}

const STATUS_STYLE = {
  open: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  last_spots: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  waitlist_only: "border-sky-400/30 bg-sky-400/10 text-sky-300",
  closed: "border-rose-400/30 bg-rose-400/10 text-rose-300",
};

const DEFAULT_EVENT_CATEGORIES = [
  {
    id: "fallback-prcc-rocket-1v1",
    name: "PRCC 2026 · Rocket League 1v1",
    mode: "solo",
    status: "open",
    events: { name: "Puerto Rico Comic Con 2026" },
  },
  {
    id: "fallback-prcc-rocket-3v3",
    name: "PRCC 2026 · Rocket League 3v3",
    mode: "team",
    status: "open",
    events: { name: "Puerto Rico Comic Con 2026" },
  },
  {
    id: "fallback-prcc-mkx",
    name: "PRCC 2026 · Mortal Kombat X",
    mode: "solo",
    status: "open",
    events: { name: "Puerto Rico Comic Con 2026" },
  },
  {
    id: "fallback-prcc-fc26",
    name: "PRCC 2026 · FC 26",
    mode: "solo",
    status: "open",
    events: { name: "Puerto Rico Comic Con 2026" },
  },
  {
    id: "fallback-prcc-starcraft",
    name: "PRCC 2026 · StarCraft",
    mode: "solo",
    status: "open",
    events: { name: "Puerto Rico Comic Con 2026" },
  },
];

export default function RegistrationPage() {
  useEffect(() => {
    document.title = "Registro PREL | Puerto Rico Esports League";

    const setMeta = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property='${name}']` : `meta[name='${name}']`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", "Portal de registro de Puerto Rico Esports League para activaciones y torneos cuando haya categorías activas publicadas.");
    setMeta("og:title", "Registro PREL | Puerto Rico Esports League", true);
    setMeta("og:description", "Registro de eventos y activaciones de Puerto Rico Esports League.", true);
    setMeta("og:type", "website", true);
    setMeta("og:url", "https://www.puertoricoesportleague.com/registro", true);
    setMeta("og:image", "https://www.puertoricoesportleague.com/logo.png", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("robots", "noindex,follow");

    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.puertoricoesportleague.com/registro");
  }, []);

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const [form, setForm] = useState({
    fullName: "",
    gamerTag: "",
    email: "",
    phone: "",
    city: "",
    dateOfBirth: "",
    tournamentType: "solo",
    teamName: "",
    eventCategoryId: "",
    mediaRelease: true,
    termsAccepted: false,
  });

  useEffect(() => {
    let cancelled = false;

    async function loadCategories() {
      if (!hasSupabaseConfig || !supabase) {
        if (!cancelled) {
          setCategories(DEFAULT_EVENT_CATEGORIES);
          setForm((prev) => ({
            ...prev,
            eventCategoryId: prev.eventCategoryId || DEFAULT_EVENT_CATEGORIES[0].id,
            tournamentType: prev.tournamentType || DEFAULT_EVENT_CATEGORIES[0].mode,
          }));
        }
        setLoadingCategories(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("event_categories")
          .select(`id,name,mode,status,events(name,status)`)
          .in("status", ["open", "last_spots", "waitlist_only"])
          .order("name", { ascending: true });

        if (error) throw error;
        if (!cancelled) {
          const next = (data && data.length ? data : DEFAULT_EVENT_CATEGORIES);
          setCategories(next);
          setForm((prev) => ({
            ...prev,
            eventCategoryId: prev.eventCategoryId || next[0].id,
            tournamentType: next[0].mode || prev.tournamentType,
          }));
        }
      } catch (err) {
        if (!cancelled) setMessage(`No se pudieron cargar categorías: ${err.message}`);
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    }

    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === form.eventCategoryId),
    [categories, form.eventCategoryId]
  );

  const requires18Plus = useMemo(() => {
    const source = `${selectedCategory?.name || ""}`.toLowerCase();
    return source.includes("18+") || source.includes("18 plus") || source.includes("adults");
  }, [selectedCategory]);

  const usingFallbackCategories = useMemo(
    () => categories.some((c) => String(c.id || "").startsWith("fallback-")),
    [categories]
  );

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function sendConfirmationWebhook(payload) {
    try {
      const response = await fetch("/api/registration-confirmation", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const detail = await response.text();
        console.warn("registration confirmation email failed", detail);
      }
    } catch (error) {
      console.warn("registration confirmation email error", error);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setMessage("");

    if (!hasSupabaseConfig || !supabase) {
      setMessage("Falta configurar Supabase en .env (REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY).");
      return;
    }

    if (!form.termsAccepted) {
      setMessage("Debes aceptar términos y privacidad para completar el registro.");
      return;
    }

    if (!form.eventCategoryId) {
      setMessage("Selecciona un evento/categoría.");
      return;
    }

    if (requires18Plus) {
      const age = calculateAge(form.dateOfBirth);
      if (!form.dateOfBirth || age === null || age < 18) {
        setMessage("Este evento es 18+. Debes tener 18 años o más para registrarte.");
        return;
      }
    }

    const [firstName, ...rest] = form.fullName.trim().split(" ");
    const lastName = rest.join(" ") || "N/A";

    setSubmitting(true);
    try {
      const { data: participant, error: participantError } = await supabase
        .from("participants")
        .insert({
          first_name: firstName || "N/A",
          last_name: lastName,
          display_name: form.gamerTag || form.fullName,
          email: form.email,
          phone: form.phone || null,
          city: form.city || null,
          date_of_birth: form.dateOfBirth || null,
          country: "PR",
        })
        .select("id")
        .single();

      if (participantError) throw participantError;

      const registrationType = form.tournamentType === "team" || form.tournamentType === "duo" ? "team" : "individual";
      let teamId = null;

      if (registrationType === "team") {
        const { data: team, error: teamError } = await supabase
          .from("teams")
          .insert({
            event_category_id: form.eventCategoryId,
            name: form.teamName || `${form.gamerTag} Team`,
            captain_participant_id: participant.id,
            status: "pending_review",
          })
          .select("id")
          .single();

        if (teamError) throw teamError;
        teamId = team.id;

        const { error: teamMemberError } = await supabase.from("team_members").insert({
          team_id: teamId,
          participant_id: participant.id,
          role: "captain",
          slot_status: "confirmed",
        });
        if (teamMemberError) throw teamMemberError;
      }

      const { data: registration, error: registrationError } = await supabase
        .from("registrations")
        .insert({
          event_category_id: form.eventCategoryId,
          registration_type: registrationType,
          participant_id: registrationType === "individual" ? participant.id : null,
          team_id: teamId,
          status: "pending",
          approval_mode: "auto",
        })
        .select("id")
        .single();

      if (registrationError) throw registrationError;

      const { error: consentError } = await supabase.from("registration_consents").insert({
        registration_id: registration.id,
        terms_version: "v1",
        privacy_version: "v1",
        media_release_accepted: Boolean(form.mediaRelease),
      });
      if (consentError) throw consentError;

      await sendConfirmationWebhook({
        registrationId: registration.id,
        participantEmail: form.email,
        participantName: form.fullName,
        gamerTag: form.gamerTag,
        categoryId: form.eventCategoryId,
        categoryName: selectedCategory?.name,
        eventName: selectedCategory?.events?.name,
      });

      setMessage(`✅ Registro enviado. ID: ${registration.id}. Te estaremos enviando confirmación por email.`);
      setForm((prev) => ({
        ...prev,
        fullName: "",
        gamerTag: "",
        email: "",
        phone: "",
        city: "",
        dateOfBirth: "",
        teamName: "",
        termsAccepted: false,
      }));
    } catch (err) {
      setMessage(`❌ Error enviando registro: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  const isSuccess = message.startsWith("✅");

  return (
    <div className="min-h-screen bg-[#080A10] text-[#F0EEE8]">
      <SiteHeader current="/registro" />

      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_80%_20%,rgba(0,42,143,0.16)_0%,transparent_65%),radial-gradient(ellipse_50%_60%_at_10%_80%,rgba(206,17,38,0.10)_0%,transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.014)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.014)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <main className="max-w-[700px] mx-auto px-4 sm:px-6 py-8 sm:py-10 md:py-14">
        <a href="/" className="text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.2em] text-slate-400 hover:text-white">← Volver al home</a>

        <header className="mt-5 sm:mt-6 mb-6 sm:mb-8">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.28em] sm:tracking-[0.35em] text-[#CE1126]">Puerto Rico Esports League</p>
          <h1 className="mt-3 text-[clamp(2rem,9vw,4rem)] leading-[0.98] font-black uppercase break-words">Registro PREL</h1>
          <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed">Escoge tu categoría, llena tus datos y completa tu registro para eventos y activaciones de Puerto Rico Esports League cuando haya cupos activos.</p>
          <p className="mt-3 inline-block max-w-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-sm leading-relaxed text-emerald-300">Participar no tiene costo. El registro es gratis.</p>
        </header>

        <section className="border border-white/10 bg-[#141828] border-t-2 border-t-[#CE1126] p-4 sm:p-5 md:p-8">
          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.24em] sm:tracking-[0.3em] text-[#CE1126]">Evento</p>
                  <div className="h-px bg-white/10 flex-1" />
                </div>

                <label className="block text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-400 mb-1">Evento / Categoría</label>
                <select
                  value={form.eventCategoryId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    const cat = categories.find((c) => c.id === nextId);
                    updateField("eventCategoryId", nextId);
                    if (cat?.mode) updateField("tournamentType", cat.mode);
                  }}
                  disabled={loadingCategories}
                  className="w-full bg-[#0F1220] border border-white/10 rounded-lg px-3 py-3 text-sm sm:text-base outline-none focus:border-[#CE1126]/60"
                >
                  {!categories.length ? <option value="">No hay categorías disponibles</option> : null}
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>

                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedCategory ? (
                    <div className={`inline-flex items-center gap-2 px-3 py-1 text-[10px] sm:text-[11px] uppercase tracking-[0.14em] sm:tracking-[0.18em] border ${STATUS_STYLE[selectedCategory.status] || STATUS_STYLE.open}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      Estado: {selectedCategory.status}
                      {requires18Plus ? " · 18+" : ""}
                    </div>
                  ) : null}
                  <div className="inline-flex items-center px-3 py-1 text-[10px] sm:text-[11px] uppercase tracking-[0.14em] sm:tracking-[0.18em] border border-emerald-400/30 bg-emerald-400/10 text-emerald-300">
                    Gratis
                  </div>
                </div>
                <p className="mt-2 text-xs text-slate-400 leading-relaxed">Registro gratuito, no hace falta pagar para participar.</p>
                {usingFallbackCategories ? (
                  <p className="mt-2 text-xs text-amber-300 leading-relaxed">Mostrando categorías base mientras conectamos categorías publicadas.</p>
                ) : null}
              </div>

              <div>
                <label className="block text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-400 mb-1">Tipo de torneo</label>
                <select
                  value={form.tournamentType}
                  onChange={(e) => updateField("tournamentType", e.target.value)}
                  className="w-full bg-[#0F1220] border border-white/10 rounded-lg px-3 py-3 text-sm sm:text-base outline-none focus:border-[#CE1126]/60"
                >
                  <option value="solo">Solo</option>
                  <option value="duo">Dúo</option>
                  <option value="team">Team</option>
                  <option value="activation">Activación</option>
                </select>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.24em] sm:tracking-[0.3em] text-[#CE1126]">Información personal</p>
                <div className="h-px bg-white/10 flex-1" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-400 mb-1">Nombre completo</label>
                  <input required value={form.fullName} onChange={(e) => updateField("fullName", e.target.value)} className="w-full bg-[#0F1220] border border-white/10 rounded-lg px-3 py-3 text-sm sm:text-base outline-none focus:border-[#CE1126]/60" />
                </div>
                <div>
                  <label className="block text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-400 mb-1">Gamer tag</label>
                  <input required value={form.gamerTag} onChange={(e) => updateField("gamerTag", e.target.value)} className="w-full bg-[#0F1220] border border-white/10 rounded-lg px-3 py-3 text-sm sm:text-base outline-none focus:border-[#CE1126]/60" />
                </div>
                <div>
                  <label className="block text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-400 mb-1">Email</label>
                  <input required type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} className="w-full bg-[#0F1220] border border-white/10 rounded-lg px-3 py-3 text-sm sm:text-base outline-none focus:border-[#CE1126]/60" />
                </div>
                <div>
                  <label className="block text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-400 mb-1">Teléfono</label>
                  <input value={form.phone} onChange={(e) => updateField("phone", e.target.value)} className="w-full bg-[#0F1220] border border-white/10 rounded-lg px-3 py-3 text-sm sm:text-base outline-none focus:border-[#CE1126]/60" />
                </div>
                <div>
                  <label className="block text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-400 mb-1">Municipio</label>
                  <input value={form.city} onChange={(e) => updateField("city", e.target.value)} className="w-full bg-[#0F1220] border border-white/10 rounded-lg px-3 py-3 text-sm sm:text-base outline-none focus:border-[#CE1126]/60" />
                </div>
                <div>
                  <label className="block text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-400 mb-1">Fecha de nacimiento {requires18Plus ? "(requerida)" : "(opcional)"}</label>
                  <input type="date" value={form.dateOfBirth} onChange={(e) => updateField("dateOfBirth", e.target.value)} className="w-full bg-[#0F1220] border border-white/10 rounded-lg px-3 py-3 text-sm sm:text-base outline-none focus:border-[#CE1126]/60" />
                </div>
              </div>

              {(form.tournamentType === "team" || form.tournamentType === "duo") ? (
                <div>
                  <label className="block text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-400 mb-1">Nombre de Team</label>
                  <input value={form.teamName} onChange={(e) => updateField("teamName", e.target.value)} className="w-full bg-[#0F1220] border border-white/10 rounded-lg px-3 py-3 text-sm sm:text-base outline-none focus:border-[#CE1126]/60" />
                </div>
              ) : null}

              <div className="pt-2 border-t border-white/10 space-y-3">
                <label className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                  <input className="mt-1" type="checkbox" checked={form.mediaRelease} onChange={(e) => updateField("mediaRelease", e.target.checked)} />
                  <span>Acepto release de imagen y uso de contenido audiovisual del evento.</span>
                </label>

                <label className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                  <input className="mt-1" type="checkbox" checked={form.termsAccepted} onChange={(e) => updateField("termsAccepted", e.target.checked)} />
                  <span>Acepto términos y política de privacidad.</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 rounded-xl bg-[#CE1126] hover:bg-[#a50e1e] transition uppercase tracking-[0.1em] sm:tracking-[0.12em] text-sm sm:text-base font-bold disabled:opacity-60"
              >
                {submitting ? "Enviando..." : "Enviar registro"}
              </button>
            </form>
          ) : (
            <div className="text-center py-8 sm:py-10">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-2 border-[#CE1126] mx-auto flex items-center justify-center text-xl sm:text-2xl">✓</div>
              <h3 className="mt-5 text-3xl sm:text-4xl font-black uppercase">¡Registro enviado!</h3>
              <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-md mx-auto leading-relaxed">Tu inscripción fue recibida. Te estaremos enviando confirmación por email.</p>
              <button
                type="button"
                onClick={() => setMessage("")}
                className="mt-6 px-5 py-3 border border-[#CE1126]/40 text-[#CE1126] uppercase tracking-[0.14em] sm:tracking-[0.18em] text-xs hover:border-[#CE1126]"
              >
                Registrar otro participante
              </button>
            </div>
          )}

          {message && !isSuccess ? <p className="text-sm mt-4 text-slate-200 leading-relaxed">{message}</p> : null}
        </section>
      </main>
    </div>
  );
}
